import { useQuery } from "@tanstack/react-query";
import { AnalyticsService } from "@/services/analytics/AnalyticsService";
import { InsightsService } from "@/services/analytics/InsightsService";

import type { ConversationDigest, NeedsAttention, WeeklySummary } from "@/types/activity";

const FIVE_MINUTES = 5 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;

export const useAgentAnalytics = (userId?: string, filterByUserId?: string) => {
  return useQuery({
    queryKey: ["agent-analytics", userId, filterByUserId],
    queryFn: () => AnalyticsService.getAgentAnalytics({ userId, filterByUserId }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useAnalyticsOverview = (userId?: string, filterByUserId?: string) => {
  return useQuery({
    queryKey: ["analytics-overview", userId, filterByUserId],
    queryFn: () => AnalyticsService.getAnalyticsOverview({ userId, filterByUserId }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useAgentTimeline = (agentId: string, startDate: Date, endDate: Date) => {
  return useQuery({
    queryKey: ["agent-timeline", agentId, startDate.toISOString(), endDate.toISOString()],
    queryFn: () => AnalyticsService.getAgentTimeline(agentId, startDate, endDate),
    enabled: !!agentId,
    staleTime: 2 * 60 * 1000, // 2 minutes for timeline data
    gcTime: 5 * 60 * 1000,
  });
};

export const useMultipleAgentTimelines = (agentIds: string[], startDate: Date, endDate: Date) => {
  return useQuery({
    queryKey: ["multiple-agent-timelines", agentIds, startDate.toISOString(), endDate.toISOString()],
    queryFn: () => AnalyticsService.getMultipleAgentTimelines(agentIds, startDate, endDate),
    enabled: agentIds.length > 0,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useWeeklySummary = () => {
  return useQuery<WeeklySummary>({
    queryKey: ["activity-insights", "weekly-summary"],
    queryFn: () => InsightsService.getWeeklySummary(),
    staleTime: FIVE_MINUTES,
    retry: 1,
  });
};

export const useNeedsAttention = () => {
  return useQuery<NeedsAttention>({
    queryKey: ["activity-insights", "needs-attention"],
    queryFn: () => InsightsService.getNeedsAttention(),
    staleTime: FIVE_MINUTES,
    retry: 1,
  });
};

/**
 * AI-synthesized "story this period" digest. Cached aggressively (30 min) so navigating
 * back to Insights doesn't re-run the synthesis — it's a relatively expensive call.
 */
export const useConversationDigest = (
  params: { agentId?: string | null; dateRange: string },
  options?: { enabled?: boolean },
) => {
  return useQuery<ConversationDigest>({
    queryKey: ["activity-insights", "conversation-digest", params.agentId ?? "all", params.dateRange],
    queryFn: () => InsightsService.getConversationDigest(params),
    staleTime: THIRTY_MINUTES,
    gcTime: THIRTY_MINUTES,
    enabled: options?.enabled ?? true,
    retry: 1,
  });
};
